import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import TextEditor from '../../../../shared/editor/text-editor';
import { useRef } from 'react';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { Editor } from 'tinymce';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface StepVacationResponderProps {
  control: Control<BulkOpsFormValues>;
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  errors: FieldErrors<BulkOpsFormValues>;
}

export default function StepVacationResponder({
  control,
  setValue,
  watch,
  errors,
}: StepVacationResponderProps) {
  const editorRef = useRef<Editor | null>(null);
  const isEnableAutoReplyCheckbox = watch(
    'vacationResponderData.enableAutoReply'
  );
  const isRestrictToContactsCheckbox = watch(
    'vacationResponderData.restrictToContacts'
  );
  const isRestrictToDomainCheckbox = watch(
    'vacationResponderData.restrictToDomain'
  );

  return (
    <Stack gap={1} width={'100%'} flex={1}>
      <Stack flexDirection="row" alignItems="center" gap={2}>
        <Typography>Auto Reply: </Typography>
        <Stack flexDirection="row" alignItems="center">
          <Typography>Disabled</Typography>
          <Controller
            name={'vacationResponderData.enableAutoReply'}
            control={control}
            render={({ field }) => {
              return (
                <Switch
                  onChange={() => {
                    setValue(
                      'vacationResponderData.enableAutoReply',
                      !isEnableAutoReplyCheckbox,
                      {
                        shouldDirty: true,
                      }
                    );
                  }}
                  checked={field.value === true}
                />
              );
            }}
          />
          <Typography>Enabled</Typography>
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap={2}>
        <Controller
          name={'vacationResponderData.restrictToContacts'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => {
                      setValue(
                        'vacationResponderData.restrictToContacts',
                        !isRestrictToContactsCheckbox,
                        {
                          shouldDirty: true,
                        }
                      );
                    }}
                    checked={field.value === true}
                  />
                }
                label="Restrict Response to User's Contacts"
              />
            );
          }}
        />
        <Controller
          name={'vacationResponderData.restrictToDomain'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => {
                      setValue(
                        'vacationResponderData.restrictToDomain',
                        !isRestrictToDomainCheckbox,
                        {
                          shouldDirty: true,
                        }
                      );
                    }}
                    checked={field.value === true}
                  />
                }
                label="Restrict Response to Domain"
              />
            );
          }}
        />
      </Stack>
      <Stack flexDirection="row" gap={2}>
        <Controller
          name={'vacationResponderData.responseSubject'}
          control={control}
          render={({ field }) => (
            <TextField
              id="responseSubject"
              label="Subject"
              required
              {...field}
              sx={{ width: 300 }}
              autoComplete="off"
              error={!!errors.vacationResponderData?.responseSubject}
              helperText={
                errors?.vacationResponderData?.responseSubject?.message || ' '
              }
            />
          )}
        />
        <Controller
          name={'dateRange'}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DateRangePicker
              {...field}
              disableHighlightToday
              sx={{ width: 400 }}
              label={'Vacation Dates'}
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                textField: {
                  error: !!error?.message,
                  helperText: error?.message ? error.message : ' ',
                },
              }}
            />
          )}
        />
      </Stack>
      <Box
        sx={
          errors.vacationResponderData?.responseBodyHtml
            ? {
                width: '100%',
                flex: 1,
                border: (theme) => `1px solid ${theme.palette.error.main}`,
                borderRadius: 3,
              }
            : { width: '100%', flex: 1 }
        }
      >
        <Controller
          name="vacationResponderData.responseBodyHtml"
          control={control}
          render={({ field }) => (
            <TextEditor
              editorRef={editorRef}
              content={field.value!}
              setContent={field.onChange}
            />
          )}
        />
      </Box>
      <FormHelperText error>
        {errors.vacationResponderData?.responseBodyHtml
          ? errors.vacationResponderData?.responseBodyHtml.message
          : ' '}
      </FormHelperText>
    </Stack>
  );
}
